// app/frontend/hooks/useMessageTranslation.ts

import { useState, useCallback, useEffect } from 'react'
import type { TranslationState, VisitorLanguage, Message } from '@/types/conversation'

interface UseMessageTranslationOptions {
  accountSlug: string
  /** Identifies the active conversation; changing it resets translation state */
  conversationId: string
  initialMessages: Message[]
  /** Conversation-level live translate settings */
  liveTranslateEnabled?: boolean
  /** Confirmed language for the conversation (when live translate is enabled) */
  detectedLanguage?: string | null
  detectedLanguageName?: string | null
}

interface UseMessageTranslationReturn {
  translations: Record<string, TranslationState>
  visitorLanguage: VisitorLanguage | null
  translateOnSend: boolean
  setTranslateOnSend: (value: boolean) => void
  handleTranslate: (messageId: string) => Promise<void>
  /** Auto-translate a message (used when live translate is enabled) */
  autoTranslateMessage: (messageId: string) => Promise<void>
}

interface DerivedTranslationState {
  translations: Record<string, TranslationState>
  visitorLanguage: VisitorLanguage | null
  translateOnSend: boolean
}

/**
 * Derives the per-conversation translation state from its messages and
 * live-translate settings. Pure so it can drive both the initial state and the
 * reset that runs when the agent switches conversations (BC-103).
 */
export function deriveTranslationState(
  messages: Message[],
  liveTranslateEnabled: boolean,
  detectedLanguage?: string | null,
  detectedLanguageName?: string | null
): DerivedTranslationState {
  const translations: Record<string, TranslationState> = {}
  messages.forEach((msg) => {
    if (msg.translation && msg.sender_type === 'Visitor') {
      translations[msg.id] = {
        translated: msg.translation.translated,
        sourceLanguage: msg.translation.source_language,
        sourceLanguageName: msg.translation.source_language_name,
        showOriginal: false
      }
    }
  })

  let visitorLanguage: VisitorLanguage | null = null
  if (liveTranslateEnabled && detectedLanguage && detectedLanguageName) {
    // Use conversation-level language if live translate is enabled
    visitorLanguage = { code: detectedLanguage, name: detectedLanguageName }
  } else {
    // Fall back to detecting from messages
    for (const msg of messages) {
      if (msg.translation && msg.sender_type === 'Visitor' && msg.translation.source_language) {
        visitorLanguage = {
          code: msg.translation.source_language,
          name: msg.translation.source_language_name
        }
        break
      }
    }
  }

  // Auto-enable when live translate is on, or when prior visitor messages were translated
  const translateOnSend = liveTranslateEnabled || messages.some(
    (msg) => msg.translation && msg.sender_type === 'Visitor' && msg.translation.source_language
  )

  return { translations, visitorLanguage, translateOnSend }
}

export function useMessageTranslation({
  accountSlug,
  conversationId,
  initialMessages,
  liveTranslateEnabled = false,
  detectedLanguage,
  detectedLanguageName
}: UseMessageTranslationOptions): UseMessageTranslationReturn {
  // Compute the initial per-conversation state once on mount; conversation
  // switches are handled by the reset effect below.
  const [initial] = useState(
    () => deriveTranslationState(initialMessages, liveTranslateEnabled, detectedLanguage, detectedLanguageName)
  )

  const [translations, setTranslations] = useState<Record<string, TranslationState>>(initial.translations)
  const [visitorLanguage, setVisitorLanguage] = useState<VisitorLanguage | null>(initial.visitorLanguage)
  const [translateOnSend, setTranslateOnSend] = useState(initial.translateOnSend)

  // Re-derive all translation state when the conversation changes. Inertia reuses
  // the Conversation page component across conversation switches, so without this
  // reset the previous conversation's visitorLanguage / translateOnSend leak into
  // the next one and translate outgoing replies into the wrong language (BC-103).
  // Keyed only on conversationId: the other props update together on navigation,
  // and we must not re-derive (and clobber manual toggles) on unrelated re-renders.
  useEffect(() => {
    const derived = deriveTranslationState(initialMessages, liveTranslateEnabled, detectedLanguage, detectedLanguageName)
    setTranslations(derived.translations)
    setVisitorLanguage(derived.visitorLanguage)
    setTranslateOnSend(derived.translateOnSend)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId])

  const handleTranslate = useCallback(async (messageId: string) => {
    // Toggle if already translated
    if (translations[messageId]?.translated) {
      setTranslations((prev) => ({
        ...prev,
        [messageId]: {
          ...prev[messageId],
          showOriginal: !prev[messageId].showOriginal
        }
      }))
      return
    }

    // Start loading
    setTranslations((prev) => ({
      ...prev,
      [messageId]: { loading: true }
    }))

    try {
      const response = await fetch(`/${accountSlug}/translations/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
        },
        body: JSON.stringify({ message_id: messageId })
      })

      const data = await response.json()

      if (!response.ok) {
        setTranslations((prev) => ({
          ...prev,
          [messageId]: { error: data.error || 'Translation failed', loading: false }
        }))
        return
      }

      if (data.skipped) {
        setTranslations((prev) => ({
          ...prev,
          [messageId]: {
            translated: data.original,
            sourceLanguage: data.source_language,
            sourceLanguageName: 'Same language',
            loading: false
          }
        }))
      } else {
        setTranslations((prev) => ({
          ...prev,
          [messageId]: {
            translated: data.translated,
            sourceLanguage: data.source_language,
            sourceLanguageName: data.source_language_name,
            loading: false,
            showOriginal: false
          }
        }))
        if (data.source_language && data.source_language_name) {
          setVisitorLanguage({ code: data.source_language, name: data.source_language_name })
          setTranslateOnSend(true)
        }
      }
    } catch {
      setTranslations((prev) => ({
        ...prev,
        [messageId]: { error: 'Failed to translate message', loading: false }
      }))
    }
  }, [accountSlug])

  // Auto-translate a message (used for live translate - doesn't toggle, just translates)
  const autoTranslateMessage = useCallback(async (messageId: string) => {
    // Skip if already translated or loading
    if (translations[messageId]?.translated || translations[messageId]?.loading) {
      return
    }

    // Start loading
    setTranslations((prev) => ({
      ...prev,
      [messageId]: { loading: true }
    }))

    try {
      const response = await fetch(`/${accountSlug}/translations/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '',
        },
        body: JSON.stringify({ message_id: messageId })
      })

      const data = await response.json()

      if (!response.ok) {
        setTranslations((prev) => ({
          ...prev,
          [messageId]: { error: data.error || 'Translation failed', loading: false }
        }))
        return
      }

      if (data.skipped) {
        setTranslations((prev) => ({
          ...prev,
          [messageId]: {
            translated: data.original,
            sourceLanguage: data.source_language,
            sourceLanguageName: 'Same language',
            loading: false
          }
        }))
      } else {
        setTranslations((prev) => ({
          ...prev,
          [messageId]: {
            translated: data.translated,
            sourceLanguage: data.source_language,
            sourceLanguageName: data.source_language_name,
            loading: false,
            showOriginal: false
          }
        }))
      }
    } catch {
      setTranslations((prev) => ({
        ...prev,
        [messageId]: { error: 'Failed to translate message', loading: false }
      }))
    }
  }, [accountSlug])

  return {
    translations,
    visitorLanguage,
    translateOnSend,
    setTranslateOnSend,
    handleTranslate,
    autoTranslateMessage
  }
}
